import "react-advanced-cropper/dist/style.css"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@suraasa/placebo-ui"
import AsyncBuilder from "components/AsyncBuilder"
import { useEffect, useRef, useState } from "react"
import { CircleStencil, Cropper, CropperRef } from "react-advanced-cropper"

interface ImageCropperProps {
  src: File | null
  onSubmit: (croppedImage: File) => Promise<void>
  onClose: () => void
}

const ImageCropper = ({ src, onSubmit, onClose }: ImageCropperProps) => {
  const [imageSrc, setImageSrc] = useState<string>("")
  const cropperRef = useRef<CropperRef>(null)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (src) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(true)
      const objectUrl = URL.createObjectURL(src)
      setImageSrc(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    } else {
      setIsOpen(false)
      setImageSrc("")
    }
  }, [src])

  const handleClose = async () => {
    setIsOpen(false)
    setImageSrc("")
    onClose()
  }

  const handleSubmit = async () => {
    if (!cropperRef.current) return

    try {
      const canvas = cropperRef.current.getCanvas()
      if (!canvas) return

      canvas.toBlob(async blob => {
        if (!blob) return

        const croppedFile = new File([blob], src?.name || "cropped-image.jpg", {
          type: blob.type,
          lastModified: Date.now(),
        })

        await onSubmit(croppedFile)
      })
      await handleClose()
    } catch (error) {
      console.error("Error submitting cropped image:", error)
    }
  }

  if (!src || !imageSrc) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle asChild={false}>{"Adjust your picture"}</DialogTitle>
        </DialogHeader>
        <DialogBody className="!max-h-[80vh]">
          <div className="mx-auto aspect-square max-h-[60vh] w-full max-w-[90vw]">
            <Cropper
              ref={cropperRef}
              src={imageSrc}
              stencilComponent={CircleStencil}
              className="size-full"
              onReady={() => {
                setTimeout(() => {
                  cropperRef.current?.refresh()
                }, 100)
              }}
              backgroundWrapperProps={{
                scaleImage: false,
                moveImage: false,
              }}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <AsyncBuilder
            handler={handleSubmit}
            render={({ onClick, loading }) => {
              return (
                <Button
                  size="sm"
                  onClick={onClick}
                  color="black"
                  loading={loading}
                >
                  Upload
                </Button>
              )
            }}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ImageCropper
